import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Bot, History, FileText } from "lucide-react";
import LiveTranscriptViewer from "@/components/noter/LiveTranscriptViewer";
import RollingSummaryPanel from "@/components/noter/RollingSummaryPanel";
import ActionItemsList from "@/components/noter/ActionItemsList";
import EVETCoPilotPanel from "@/components/noter/EVETCoPilotPanel";
import NoterControlPanel from "@/components/noter/NoterControlPanel";
import NoterSettingsModal from "@/components/noter/NoterSettingsModal";
import ExportDialog from "@/components/noter/ExportDialog";
import AudioFileUploader from "@/components/noter/AudioFileUploader";

const WS_BASE = process.env.REACT_APP_WS_URL || process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws") || "ws://localhost:8001";

export default function NoterHub() {
  const { user, token } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | active | paused | completed
  const [title, setTitle] = useState("Untitled Meeting");
  const [segments, setSegments] = useState([]);
  const [partialText, setPartialText] = useState("");
  const [summary, setSummary] = useState({ key_points: [], decisions: [], content: "" });
  const [actionItems, setActionItems] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previousSessions, setPreviousSessions] = useState([]);
  const [activeTab, setActiveTab] = useState("live");
  const [settings, setSettings] = useState({
    language: null,
    title: "Untitled Meeting",
    sampleRate: 16000,
    transcriptionMode: "live",
    targetLanguage: "",
    ttsVoice: "off",
  });

  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const durationTimerRef = useRef(null);
  const audioQueueRef = useRef([]);
  const sendingRef = useRef(false);

  // ── Timer ──
  useEffect(() => {
    if (status === "active") {
      durationTimerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
    } else {
      clearInterval(durationTimerRef.current);
    }
    return () => clearInterval(durationTimerRef.current);
  }, [status]);

  // ── Fetch previous sessions on mount ──
  useEffect(() => {
    fetchPreviousSessions();
  }, []);

  const fetchPreviousSessions = async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const [activeRes, completedRes] = await Promise.all([
        fetch(`${API}/noter/sessions`, { headers: h, credentials: "include" }),
        fetch(`${API}/noter/sessions/completed?limit=5`, { headers: h, credentials: "include" }),
      ]);
      const active = (await activeRes.json()).sessions || [];
      const completed = (await completedRes.json()).sessions || [];
      setPreviousSessions([...active, ...completed]);
    } catch {
      // Silently fail
    }
  };

  // ── Send audio queue ──
  const sendAudioQueue = useCallback(async () => {
    if (sendingRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    sendingRef.current = true;
    while (audioQueueRef.current.length > 0) {
      const chunk = audioQueueRef.current.shift();
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(chunk);
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    sendingRef.current = false;
  }, []);

  // ── Start session ──
  const startSession = async () => {
    setLoading(true);
    setSegments([]);
    setPartialText("");
    setSummary({ key_points: [], decisions: [], content: "" });
    setActionItems([]);
    setContradictions([]);
    setDuration(0);
    setTitle(settings.title || "Untitled Meeting");

    try {
      const wsUrl = `${WS_BASE}/ws/noter/live`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      await new Promise((resolve, reject) => {
        ws.onopen = resolve;
        ws.onerror = reject;
        setTimeout(() => reject(new Error("WebSocket timeout")), 10000);
      });

      // Send auth message
      ws.send(JSON.stringify({
        type: "auth",
        token,
        title: settings.title || "Untitled Meeting",
        language: settings.language,
        sample_rate: settings.sampleRate,
        target_language: settings.targetLanguage,
        transcription_mode: settings.transcriptionMode,
      }));

      // Wait for session_started
      const authResponse = await new Promise((resolve, reject) => {
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "session_started") resolve(msg.data);
            else if (msg.type === "error") reject(new Error(msg.detail));
          } catch {}
        };
        setTimeout(() => reject(new Error("Auth timeout")), 15000);
      });

      setSessionId(authResponse.session_id);
      setStatus("active");

      // Start audio capture
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new AudioContext({ sampleRate: settings.sampleRate });
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      source.connect(processor);
      processor.connect(audioCtx.destination);

      processor.onaudioprocess = (e) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        const pcm = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
          pcm[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32768)));
        }
        audioQueueRef.current.push(pcm.buffer);
        if (!sendingRef.current) sendAudioQueue();
      };

      // Message handler
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          handleWsMessage(msg);
        } catch {}
      };

      ws.onclose = () => {
        setStatus((s) => (s === "active" || s === "paused" ? "completed" : s));
      };

      ws.onerror = () => {
        toast.error("WebSocket connection error");
      };

      toast.success("Noter session started");
    } catch (err) {
      toast.error(err.message || "Failed to start session");
      setStatus("idle");
    } finally {
      setLoading(false);
    }
  };

  // ── Speech Synthesis (TTS Stream Reader) ──
  const speakText = (text, gender) => {
    if (!window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();

      let voice = null;
      if (gender === "female") {
        voice = voices.find(v => {
          const name = v.name.toLowerCase();
          return name.includes("female") || name.includes("zira") || name.includes("samantha") || name.includes("hazel") || name.includes("google us english");
        });
      } else if (gender === "male") {
        voice = voices.find(v => {
          const name = v.name.toLowerCase();
          return name.includes("male") || name.includes("david") || name.includes("google uk english male") || name.includes("mark");
        });
      }
      if (voice) {
        utterance.voice = voice;
      }
      
      if (settings.targetLanguage) {
        utterance.lang = settings.targetLanguage;
      } else if (settings.language) {
        utterance.lang = settings.language;
      }

      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.error("SpeechSynthesis error:", e);
    }
  };

  // ── Handle WebSocket messages ──
  const handleWsMessage = (msg) => {
    const { type, data } = msg;
    switch (type) {
      case "transcript":
        setSegments((prev) => [...prev, data]);
        if (settings.ttsVoice && settings.ttsVoice !== "off") {
          const textToSpeak = data.translated_text || data.text;
          if (textToSpeak) {
            speakText(textToSpeak, settings.ttsVoice);
          }
        }
        break;
      case "partial":
        setPartialText(data.text);
        break;
      case "summary":
        setSummary(data);
        break;
      case "action_item":
        setActionItems((prev) => {
          if (prev.some((a) => a.description === data.description)) return prev;
          return [...prev, data];
        });
        break;
      case "contradiction":
        setContradictions((prev) => [...prev, data]);
        break;
      case "completed":
        setStatus("completed");
        break;
      case "paused":
        setStatus("paused");
        break;
      case "resumed":
        setStatus("active");
        break;
      case "status":
        if (data?.state === "paused") setStatus("paused");
        if (data?.state === "active") setStatus("active");
        break;
      case "error":
        toast.error(data?.detail || "Noter error");
        break;
    }
  };

  // ── Stop session ──
  const stopSession = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: "stop" }));
      wsRef.current.close();
    }
    cleanupAudio();
    setStatus("completed");
    setDuration(0);
    fetchPreviousSessions();
    toast.success("Session stopped");
  };

  // ── Pause / Resume ──
  const pauseSession = () => {
    wsRef.current?.send(JSON.stringify({ type: "pause" }));
  };

  const resumeSession = () => {
    wsRef.current?.send(JSON.stringify({ type: "resume" }));
  };

  // ── Cleanup audio ──
  const cleanupAudio = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSession();
    };
  }, []);

  // ── Action status update ──
  const handleActionUpdate = async (item, newStatus) => {
    setActionItems((prev) =>
      prev.map((a) => (a.description === item.description ? { ...a, status: newStatus } : a))
    );
  };

  // ── Export ──
  const handleExport = async (format) => {
    if (!sessionId) {
      toast.error("No session to export");
      return;
    }
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/noter/sessions/${sessionId}/export`, {
        method: "POST",
        headers: { ...h, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ format }),
      });
      const data = await res.json();
      if (data.content) {
        const mime = format === "json" ? "application/json" : "text/markdown";
        const blob = new Blob([data.content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `meeting-${sessionId}.${format === "json" ? "json" : "md"}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      }
    } catch {
      toast.error("Export failed");
    }
  };

  // ── Load previous session data ──
  const loadSession = async (sid) => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const [sessionRes, transcriptRes, actionsRes, contradictionsRes] = await Promise.all([
        fetch(`${API}/noter/sessions/${sid}`, { headers: h, credentials: "include" }),
        fetch(`${API}/noter/sessions/${sid}/transcript?limit=500`, { headers: h, credentials: "include" }),
        fetch(`${API}/noter/sessions/${sid}/actions`, { headers: h, credentials: "include" }),
        fetch(`${API}/noter/sessions/${sid}/contradictions`, { headers: h, credentials: "include" }),
      ]);
      const sessionData = await sessionRes.json();
      const transcriptData = await transcriptRes.json();
      const actionsData = await actionsRes.json();
      const contradictionsData = await contradictionsRes.json();

      setSessionId(sid);
      setTitle(sessionData.title || "Meeting");
      setSegments(transcriptData.segments || []);
      setActionItems(actionsData.action_items || []);
      setContradictions(contradictionsData.contradictions || []);
      setSummary(sessionData.summary || {});
      setStatus("completed");
      setActiveTab("history");
    } catch {
      toast.error("Failed to load session");
    }
  };

  return (
    <div className="min-h-screen bg-app">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">AllBaise Noter</h1>
              <p className="text-[10px] text-slate-400">Live Meeting Notetaker</p>
            </div>
            {sessionId && (
              <Badge variant="outline" className="ml-2 text-[10px] bg-app">
                {sessionId}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NoterSettingsModal
              settings={settings}
              onSave={setSettings}
            />
            <ExportDialog onExport={handleExport} />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="live" className="text-xs">
              <Bot className="w-3.5 h-3.5 mr-1" />
              Live Session
            </TabsTrigger>
            <TabsTrigger value="history" className="text-xs">
              <History className="w-3.5 h-3.5 mr-1" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="live">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left column - Transcript */}
              <div className="lg:col-span-2 space-y-4">
                <LiveTranscriptViewer segments={segments} partial={partialText} />

                {/* Summary + eVET row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RollingSummaryPanel summary={summary} />
                  <EVETCoPilotPanel contradictions={contradictions} />
                </div>
              </div>

              {/* Right column - Controls + Actions */}
              <div className="space-y-4">
                <NoterControlPanel
                  status={status}
                  duration={duration}
                  segmentCount={segments.length}
                  onStart={startSession}
                  onStop={stopSession}
                  onPause={pauseSession}
                  onResume={resumeSession}
                  loading={loading}
                />
                {(status === "idle" || status === "completed") && (
                  <AudioFileUploader token={token} onSessionLoaded={loadSession} />
                )}
                <ActionItemsList
                  actionItems={actionItems}
                  onUpdateStatus={handleActionUpdate}
                />

                {/* Stats */}
                {segments.length > 0 && (
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Session Stats</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-400">Segments:</span>
                        <span className="ml-1 font-medium">{segments.length}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Actions:</span>
                        <span className="ml-1 font-medium">{actionItems.length}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Contradictions:</span>
                        <span className="ml-1 font-medium">{contradictions.length}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Status:</span>
                        <span className="ml-1 font-medium capitalize">{status}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-white">
                <h3 className="text-sm font-semibold text-slate-700">Previous Sessions</h3>
              </div>
              <div className="p-4">
                {previousSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                    <History className="w-6 h-6 mb-1 opacity-50" />
                    <p className="text-xs">No previous sessions found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {previousSessions.map((s) => (
                      <button
                        key={s.session_id}
                        onClick={() => loadSession(s.session_id)}
                        className="w-full text-left p-3 rounded-lg border border-slate-100 hover:border-violet-200 hover:bg-violet-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{s.title || "Untitled"}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                {s.status}
                              </Badge>
                              <span className="text-[10px] text-slate-400">
                                {s.segment_count || 0} segments
                              </span>
                              {s.created_at && (
                                <span className="text-[10px] text-slate-400">
                                  {new Date(s.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <FileText className="w-4 h-4 text-slate-300" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
